/**
 * The unsent text of each conversation, kept in the browser so switching to another client, opening
 * the conversation from the Zadania panel in full, or reloading the tab does not lose a half-written
 * message.
 *
 * Read defensively (CLAUDE.md, production discipline): the record is a JSON object of `c<conversation id>`
 * → string, and anything else — a record an older build wrote, a value someone edited by hand, a
 * store that throws — reads as no drafts at all. A draft is a convenience, never state the server
 * relies on, so losing one is the worst outcome.
 */

import type { ForgettingPreferenceStore, PreferenceStore } from '~/shared/lib/preferenceStore';

export const CHAT_DRAFTS_KEY = 'movebuddy.chat.drafts.v1';

/** No conversation keeps more than this; a pasted essay is still the trainer's, but not forever. */
const MAX_DRAFT_LENGTH = 4_000;
/** Older drafts beyond this many are dropped, so the record cannot grow without bound. */
const MAX_DRAFTS = 50;

export type DraftMap = Readonly<Record<string, string>>;

/**
 * `c` + the id, never the bare number: an integer-like key is ordered numerically by every engine,
 * which would make "the oldest draft" mean "the lowest conversation id" when the record is trimmed.
 */
export function draftKey(conversationId: number): string {
  return `c${String(conversationId)}`;
}

export function draftOf(drafts: DraftMap, conversationId: number): string {
  return drafts[draftKey(conversationId)] ?? '';
}

export function readDrafts(store: PreferenceStore | null): DraftMap {
  if (store === null) return {};
  let raw: string | null;
  try {
    raw = store.getItem(CHAT_DRAFTS_KEY);
  } catch {
    return {};
  }
  if (raw === null) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!/^c\d+$/.test(key) || typeof value !== 'string' || value.trim() === '') continue;
    out[key] = value.slice(0, MAX_DRAFT_LENGTH);
  }
  return out;
}

/** The map with `conversationId`'s draft set — or removed, when the text is blank. */
export function withDraft(drafts: DraftMap, conversationId: number, text: string): DraftMap {
  const key = draftKey(conversationId);
  const next: Record<string, string> = { ...drafts };
  delete next[key];
  if (text.trim() !== '') next[key] = text.slice(0, MAX_DRAFT_LENGTH);
  const keys = Object.keys(next);
  if (keys.length > MAX_DRAFTS) {
    // Insertion order: the oldest entries are first, and the one just written is last.
    for (const stale of keys.slice(0, keys.length - MAX_DRAFTS)) delete next[stale];
  }
  return next;
}

export function writeDrafts(store: ForgettingPreferenceStore | null, drafts: DraftMap): void {
  if (store === null) return;
  try {
    if (Object.keys(drafts).length === 0) store.removeItem(CHAT_DRAFTS_KEY);
    else store.setItem(CHAT_DRAFTS_KEY, JSON.stringify(drafts));
  } catch {
    // A full or blocked store: the draft stays in memory for this visit, which is all it promised.
  }
}
